import { Card, CardBody, CardHeader, CardTitle } from '@/common'
import type { ReactNode } from 'react'
import useI18n from '@/shared/hooks/usei18n'
import type { BatchLiabilityResultItem } from '@/features/cbam-reporting/hooks/cbamLiability'
import { getLiabilityProjectionDerivedForYear } from './liabilityProjectionDerived'
import { useNetMassDisplay } from '@/shared/netMassDisplay/NetMassDisplayUnitContext'

const formatSpecificEmissions = (value: number | undefined | null) =>
  value !== undefined && value !== null ? `${value.toFixed(3)} tCO₂/t` : '-'

type CbamGoodEmissionsProjectionSectionProps = {
  liabilityData: BatchLiabilityResultItem | null
  projectionYear: number
  netMassInTonnes: number
  headerAccessory?: ReactNode
}

export default function CbamGoodEmissionsProjectionSection({
  liabilityData,
  projectionYear,
  netMassInTonnes,
  headerAccessory
}: CbamGoodEmissionsProjectionSectionProps) {
  const { t, formatCbamTco2Display } = useI18n()
  const { formatNetMass } = useNetMassDisplay()
  const formatEmissions = (value: number | undefined | null) => formatCbamTco2Display(value)

  const {
    specificDirectEmissions,
    specificIndirectEmissions,
    specificFreeAllocation,
    specificIncludedEmissions,
    totalEmissions,
    totalFreeAllocation,
    netEmissions,
    netEmissionsPerTonne
  } = getLiabilityProjectionDerivedForYear(liabilityData, projectionYear)

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <CardTitle
            title={t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.sections.emissionsAllocationAndCertificates'
            )}
          />
        </div>
        {headerAccessory ? <div className="shrink-0">{headerAccessory}</div> : null}
      </CardHeader>
      <CardBody className="space-y-4">
        <div>
          <h3 className="font-semibold mb-2">
            {t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.subsections.specificValues'
            )}
          </h3>
          <div className="text-xs text-text-secondary mb-2">
            {t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.units.specificEmissions'
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.specificDirectEmbeddedEmissions'
                )}
              </div>
              <div className="font-medium">
                {formatSpecificEmissions(specificDirectEmissions)}
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.specificIndirectEmbeddedEmissions'
                )}
              </div>
              <div className="font-medium">
                {formatSpecificEmissions(specificIndirectEmissions)}
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.specificEmbeddedFreeAllocation'
                )}
              </div>
              <div className="font-medium">
                {formatSpecificEmissions(specificFreeAllocation)}
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                <span className="font-semibold">
                  {t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.specificIncludedEmbeddedEmissions'
                  )}
                </span>
              </div>
              <div className="font-medium">
                {formatSpecificEmissions(specificIncludedEmissions)}
              </div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                <span className="font-semibold">
                  {t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.certificatesRequiredPerTonne'
                  )}
                </span>
              </div>
              <div className="font-medium">
                {formatSpecificEmissions(netEmissionsPerTonne)}
              </div>
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="font-semibold mb-2">
            {t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.subsections.scalingFactor'
            )}
          </h3>
          <div>
            <div className="text-sm text-text-secondary mb-1">
              {t(
                'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.totalNetWeight'
              )}
            </div>
            <div className="font-medium">{formatNetMass(netMassInTonnes)}</div>
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="font-semibold mb-2">
            {t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.subsections.totalValues'
            )}
          </h3>
          <div className="text-xs text-text-secondary mb-2">
            {t(
              'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.units.totalEmissions'
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-text-secondary mb-1">
                <span className="font-semibold">
                  {t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.totalIncludedEmbeddedEmissions'
                  )}
                </span>
              </div>
              <div className="font-medium">{formatEmissions(totalEmissions)}</div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                {t(
                  'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.totalEmbeddedFreeAllocation'
                )}
              </div>
              <div className="font-medium">{formatEmissions(totalFreeAllocation)}</div>
            </div>
            <div>
              <div className="text-sm text-text-secondary mb-1">
                <span className="font-semibold">
                  {t(
                    'cbamReporting:cbamReportDetails.reportedGoods.importedGoodDetailsDrawer.fields.totalCertificatesRequired'
                  )}
                </span>
              </div>
              <div className="font-medium">{formatEmissions(netEmissions)}</div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}
